import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  X, Users, Clock, DollarSign, ChefHat, ShoppingBag,
  CalendarClock, Sparkles, ArrowRight, AlertCircle,
  UserPlus, Trash2, ArrowRightLeft, Scissors, Combine
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from './ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { PrintRunningBillButton } from './PrintDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status display config
const STATUS_CONFIG = {
  available: { label: 'Available', color: 'text-muted-foreground', bg: 'bg-muted' },
  seated: { label: 'Seated', color: 'text-blue-600', bg: 'bg-blue-900/50' },
  ordering: { label: 'Ordering', color: 'text-yellow-600', bg: 'bg-yellow-900/50' },
  pending_payment: { label: 'Pending Payment', color: 'text-orange-600', bg: 'bg-orange-900/50' },
  reserved: { label: 'Reserved', color: 'text-purple-600', bg: 'bg-purple-900/50' },
  cleaning: { label: 'Cleaning', color: 'text-red-600', bg: 'bg-red-900/50' }
};

export default function TableActionDrawer({ 
  open, 
  onClose, 
  table, 
  onSeatTable,     // Opens guest count modal then creates ticket
  onResumeOrder,   // Loads existing ticket in POS
  onRefresh        // Refresh table list after state change
}) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Reserved form state
  const [showReservedForm, setShowReservedForm] = useState(false);
  const [reservedName, setReservedName] = useState('');
  const [reservedTime, setReservedTime] = useState('');
  const [reservedGuests, setReservedGuests] = useState('');
  const [reservedNotes, setReservedNotes] = useState('');
  
  // Cleaning form state
  const [showCleaningForm, setShowCleaningForm] = useState(false);
  const [cleaningNotes, setCleaningNotes] = useState('');

  // Transfer / Split / Merge state
  const [tsmMode, setTsmMode] = useState(null); // 'transfer' | 'split' | 'merge'
  const [otherTables, setOtherTables] = useState([]);
  const [ticketItems, setTicketItems] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [tsmTargetId, setTsmTargetId] = useState(null);
  const [tsmBusy, setTsmBusy] = useState(false);

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const outletId = () => table?.outlet_id || localStorage.getItem('selectedOutletId');

  const openTsm = async (mode) => {
    setTsmMode(mode);
    setTsmTargetId(null);
    setSelectedItemIds([]);
    try {
      const res = await fetch(`${API_URL}/api/tables?outlet_id=${outletId()}`, { headers: authHeaders });
      const all = res.ok ? await res.json() : [];
      setOtherTables(all.filter(t => t.id !== table.id));
      if (mode === 'split') {
        const tRes = await fetch(`${API_URL}/api/pos/tickets/${table.current_ticket_id}`, { headers: authHeaders });
        if (tRes.ok) {
          const tk = await tRes.json();
          setTicketItems((tk.items || []).filter(i => !i.is_voided));
        }
      }
    } catch { toast.error('Failed to load tables'); }
  };

  const closeTsm = () => { setTsmMode(null); setSelectedItemIds([]); setTsmTargetId(null); };

  const toggleItem = (id) => setSelectedItemIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const doTransfer = async () => {
    if (!tsmTargetId) { toast.error('Pick a table to move to'); return; }
    setTsmBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/tables/tickets/${table.current_ticket_id}/move-table`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ new_table_id: tsmTargetId }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Transfer failed');
      toast.success('Order transferred'); closeTsm(); onRefresh?.(); onClose();
    } catch (err) { toast.error(err.message); }
    setTsmBusy(false);
  };

  const doSplit = async () => {
    if (!selectedItemIds.length) { toast.error('Select at least one item'); return; }
    if (selectedItemIds.length === ticketItems.length) { toast.error('Cannot move every item — leave at least one on this bill'); return; }
    setTsmBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pos/tickets/${table.current_ticket_id}/split`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ item_ids: selectedItemIds, new_table_id: tsmTargetId || null }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Split failed');
      toast.success(`Split into ${data.new_ticket_number || 'a new bill'}`); closeTsm(); onRefresh?.(); onClose();
    } catch (err) { toast.error(err.message); }
    setTsmBusy(false);
  };

  const doMerge = async () => {
    if (!tsmTargetId) { toast.error('Pick a table to merge in'); return; }
    const source = otherTables.find(t => t.id === tsmTargetId);
    if (!source?.current_ticket_id) { toast.error('That table has no open order'); return; }
    setTsmBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pos/tickets/merge`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ ticket_ids: [table.current_ticket_id, source.current_ticket_id], target_table_id: table.id }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Merge failed');
      toast.success(`Merged ${source.name} into ${table.name}`); closeTsm(); onRefresh?.(); onClose();
    } catch (err) { toast.error(err.message); }
    setTsmBusy(false);
  };

  if (!table) return null;
  
  const displayStatus = table.display_status || table.status;
  const statusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.available;
  const hasTicket = !!table.current_ticket_id;
  const ticketSummary = table.ticket_summary;
  
  // Role checks
  const isManager = ['owner', 'store_manager', 'manager', 'finance_manager'].includes(user?.role_name);
  const canManageReserved = isManager;
  const canManageCleaning = true; // Any staff can manage cleaning
  
  // Format elapsed time
  const formatElapsed = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };
  
  // Format time for display
  const formatReservedTime = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return isoString;
    }
  };
  
  // API Calls
  const setReserved = async () => {
    setLoading(true);
    try {
      const body = {};
      if (reservedName) body.name = reservedName;
      if (reservedTime) body.time = new Date(reservedTime).toISOString();
      if (reservedGuests) body.guests = parseInt(reservedGuests);
      if (reservedNotes) body.notes = reservedNotes;
      
      const res = await fetch(`${API_URL}/api/tables/${table.id}/set-reserved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to set reserved');
      }
      
      toast.success(`Table ${table.name} marked as Reserved`);
      setShowReservedForm(false);
      clearForms();
      onRefresh?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };
  
  const clearReserved = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tables/${table.id}/clear-reserved`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to clear reserved');
      }
      
      toast.success(`Table ${table.name} reservation cleared`);
      onRefresh?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };
  
  const setCleaning = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tables/${table.id}/set-cleaning`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes: cleaningNotes || null })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to set cleaning');
      }
      
      toast.success(`Table ${table.name} marked for Cleaning`);
      setShowCleaningForm(false);
      clearForms();
      onRefresh?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };
  
  const clearCleaning = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tables/${table.id}/clear-cleaning`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to clear cleaning');
      }
      
      toast.success(`Table ${table.name} cleaning complete`);
      onRefresh?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };
  
  const clearForms = () => {
    setReservedName('');
    setReservedTime('');
    setReservedGuests('');
    setReservedNotes('');
    setCleaningNotes('');
  };
  
  const handleClose = () => {
    setShowReservedForm(false);
    setShowCleaningForm(false);
    clearForms();
    onClose();
  };
  
  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="bg-card border-border w-[400px] sm:w-[450px]" data-testid="table-action-drawer">
        <SheetHeader>
          <SheetTitle className="text-foreground flex items-center gap-3">
            <span className="text-2xl font-bold">{table.name}</span>
            <span className={`px-2 py-1 rounded text-sm ${statusConfig.bg} ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </SheetTitle>
          <SheetDescription className="text-muted-foreground">
            {table.section && <span>{table.section} • </span>}
            <Users className="w-3 h-3 inline mr-1" />
            Capacity: {table.capacity}
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Ticket Summary (if exists) */}
          {hasTicket && ticketSummary && (
            <div className="bg-muted rounded-lg p-4 space-y-3" data-testid="ticket-summary">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm text-muted-foreground">Ticket</span>
                <span className="text-foreground font-mono">{ticketSummary.ticket_number}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <Users className="w-4 h-4 mx-auto text-blue-600 mb-1" />
                  <span className="text-lg font-bold text-foreground">{ticketSummary.guest_count}</span>
                  <span className="text-xs text-muted-foreground block">Guests</span>
                </div>
                <div>
                  <Clock className="w-4 h-4 mx-auto text-yellow-600 mb-1" />
                  <span className="text-lg font-bold text-foreground">{formatElapsed(ticketSummary.elapsed_minutes)}</span>
                  <span className="text-xs text-muted-foreground block">Time</span>
                </div>
                <div>
                  <ShoppingBag className="w-4 h-4 mx-auto text-secondary mb-1" />
                  <span className="text-lg font-bold text-foreground">{ticketSummary.item_count}</span>
                  <span className="text-xs text-muted-foreground block">Items</span>
                </div>
              </div>
              {ticketSummary.total > 0 && (
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-muted-foreground">Total</span>
                  <span className="text-xl font-bold text-secondary">₱{ticketSummary.total.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Reserved Info */}
          {displayStatus === 'reserved' && (
            <div className="bg-purple-50 border border-purple-700 rounded-lg p-4" data-testid="reserved-info">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="w-5 h-5 text-purple-600" />
                <span className="text-purple-300 font-medium">Reservation Details</span>
              </div>
              {table.reserved_name && (
                <p className="text-foreground mb-1"><strong>Name:</strong> {table.reserved_name}</p>
              )}
              {table.reserved_time && (
                <p className="text-foreground mb-1"><strong>Time:</strong> {formatReservedTime(table.reserved_time)}</p>
              )}
              {table.reserved_party_size && (
                <p className="text-foreground mb-1"><strong>Party Size:</strong> {table.reserved_party_size}</p>
              )}
              {table.reserved_notes && (
                <p className="text-muted-foreground text-sm mt-2">{table.reserved_notes}</p>
              )}
            </div>
          )}
          
          {/* Cleaning Info */}
          {displayStatus === 'cleaning' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="cleaning-info">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-red-600" />
                <span className="text-red-300 font-medium">Cleaning in Progress</span>
              </div>
              {table.cleaning_started_at && (
                <p className="text-sm text-muted-foreground">
                  Started: {new Date(table.cleaning_started_at).toLocaleTimeString()}
                </p>
              )}
              {table.cleaning_notes && (
                <p className="text-muted-foreground text-sm mt-2">{table.cleaning_notes}</p>
              )}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Actions</h4>
            
            {/* Seat / Start Order - Only when AVAILABLE and no manual override */}
            {displayStatus === 'available' && !hasTicket && (
              <Button 
                className="w-full h-14 bg-secondary hover:bg-secondary/90 text-lg"
                onClick={() => {
                  handleClose();
                  onSeatTable(table);
                }}
                data-testid="seat-table-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Seat Guests / Start Order
                <ArrowRight className="w-5 h-5 ml-auto" />
              </Button>
            )}
            
            {/* Resume Order - When there's an active ticket */}
            {hasTicket && ticketSummary?.status !== 'paid' && (
              <Button 
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-lg"
                onClick={() => {
                  handleClose();
                  onResumeOrder(table);
                }}
                data-testid="resume-order-btn"
              >
                <ChefHat className="w-5 h-5 mr-2" />
                Resume Order
                <ArrowRight className="w-5 h-5 ml-auto" />
              </Button>
            )}
            
            {/* Print Running Bill (provisional, non-fiscal) - when there's an unpaid ticket */}
            {hasTicket && ticketSummary?.status !== 'paid' && (
              <PrintRunningBillButton ticketId={table.current_ticket_id} />
            )}

            {/* Transfer / Split / Merge - when there's an unpaid ticket */}
            {hasTicket && ticketSummary?.status !== 'paid' && (
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" className="h-12 flex-col gap-1 border-border" onClick={() => openTsm('transfer')} data-testid="table-transfer-btn">
                  <ArrowRightLeft className="w-4 h-4" /><span className="text-xs">Transfer</span>
                </Button>
                <Button variant="outline" className="h-12 flex-col gap-1 border-border" onClick={() => openTsm('split')} data-testid="table-split-btn">
                  <Scissors className="w-4 h-4" /><span className="text-xs">Split</span>
                </Button>
                <Button variant="outline" className="h-12 flex-col gap-1 border-border" onClick={() => openTsm('merge')} data-testid="table-merge-btn">
                  <Combine className="w-4 h-4" /><span className="text-xs">Merge</span>
                </Button>
              </div>
            )}
            
            {/* Mark Reserved - Only when available and no open ticket (manager/owner) */}
            {displayStatus === 'available' && !hasTicket && canManageReserved && (
              <Button 
                variant="outline" 
                className="w-full h-12 border-purple-500 text-purple-600 hover:bg-purple-50"
                onClick={() => setShowReservedForm(true)}
                data-testid="mark-reserved-btn"
              >
                <CalendarClock className="w-4 h-4 mr-2" />
                Mark as Reserved
              </Button>
            )}
            
            {/* Clear Reserved - Only when reserved (manager/owner) */}
            {displayStatus === 'reserved' && canManageReserved && (
              <Button 
                variant="outline" 
                className="w-full h-12 border-purple-500 text-purple-600 hover:bg-purple-50"
                onClick={clearReserved}
                disabled={loading}
                data-testid="clear-reserved-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {loading ? 'Clearing...' : 'Clear Reservation'}
              </Button>
            )}
            
            {/* Mark Cleaning - Only when available and no open ticket (any staff) */}
            {displayStatus === 'available' && !hasTicket && canManageCleaning && (
              <Button 
                variant="outline" 
                className="w-full h-12 border-red-500 text-red-600 hover:bg-red-50"
                onClick={() => setShowCleaningForm(true)}
                data-testid="mark-cleaning-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Mark for Cleaning
              </Button>
            )}
            
            {/* Clear Cleaning - Only when cleaning (any staff) */}
            {displayStatus === 'cleaning' && canManageCleaning && (
              <Button 
                className="w-full h-12 bg-green-600 hover:bg-green-700"
                onClick={clearCleaning}
                disabled={loading}
                data-testid="clear-cleaning-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {loading ? 'Clearing...' : 'Cleaning Complete'}
              </Button>
            )}
            
            {/* Info about why seating blocked */}
            {(displayStatus === 'reserved' || displayStatus === 'cleaning') && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/30 rounded">
                <AlertCircle className="w-4 h-4" />
                <span>
                  {displayStatus === 'reserved' 
                    ? 'Clear reservation to seat guests' 
                    : 'Complete cleaning to seat guests'}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Reserved Form Dialog */}
        <Dialog open={showReservedForm} onOpenChange={setShowReservedForm}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle>Reserve Table {table.name}</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Enter reservation details (all fields optional)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-muted-foreground">Guest Name</label>
                <Input 
                  value={reservedName}
                  onChange={e => setReservedName(e.target.value)}
                  placeholder="e.g., John Smith"
                  className="bg-muted border-border mt-1"
                  data-testid="reserved-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Reservation Time</label>
                <Input 
                  type="datetime-local"
                  value={reservedTime}
                  onChange={e => setReservedTime(e.target.value)}
                  className="bg-muted border-border mt-1"
                  data-testid="reserved-time-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Party Size</label>
                <Input 
                  type="number"
                  value={reservedGuests}
                  onChange={e => setReservedGuests(e.target.value)}
                  placeholder="Number of guests"
                  min={1}
                  className="bg-muted border-border mt-1"
                  data-testid="reserved-guests-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Notes</label>
                <Textarea 
                  value={reservedNotes}
                  onChange={e => setReservedNotes(e.target.value)}
                  placeholder="Any special requests..."
                  className="bg-muted border-border mt-1"
                  rows={2}
                  data-testid="reserved-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReservedForm(false)}>Cancel</Button>
              <Button 
                onClick={setReserved} 
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-reserve-btn"
              >
                {loading ? 'Reserving...' : 'Reserve Table'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Cleaning Form Dialog */}
        <Dialog open={showCleaningForm} onOpenChange={setShowCleaningForm}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle>Mark Table {table.name} for Cleaning</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Add optional notes about the cleaning
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-muted-foreground">Notes (optional)</label>
                <Textarea 
                  value={cleaningNotes}
                  onChange={e => setCleaningNotes(e.target.value)}
                  placeholder="e.g., Needs deep clean, spill on seat..."
                  className="bg-muted border-border mt-1"
                  rows={3}
                  data-testid="cleaning-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCleaningForm(false)}>Cancel</Button>
              <Button 
                onClick={setCleaning} 
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-cleaning-btn"
              >
                {loading ? 'Marking...' : 'Mark for Cleaning'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transfer / Split / Merge dialog */}
        <Dialog open={!!tsmMode} onOpenChange={(o) => { if (!o) closeTsm(); }}>
          <DialogContent className="bg-card border-border max-w-md" data-testid="table-tsm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {tsmMode === 'transfer' && <><ArrowRightLeft className="w-5 h-5 text-secondary" /> Transfer {table.name}</>}
                {tsmMode === 'split' && <><Scissors className="w-5 h-5 text-secondary" /> Split {table.name}</>}
                {tsmMode === 'merge' && <><Combine className="w-5 h-5 text-secondary" /> Merge into {table.name}</>}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {tsmMode === 'transfer' && 'Move this whole order to an available table.'}
                {tsmMode === 'split' && 'Pick the items to move onto a new bill (optionally on another table).'}
                {tsmMode === 'merge' && 'Choose another table whose order will be combined into this one.'}
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 max-h-[50vh] overflow-y-auto space-y-2">
              {/* TRANSFER: available tables */}
              {tsmMode === 'transfer' && (() => {
                const avail = otherTables.filter(t => (t.display_status || t.status) === 'available');
                if (!avail.length) return <p className="text-sm text-muted-foreground text-center py-4">No available tables.</p>;
                return avail.map(t => (
                  <button key={t.id} onClick={() => setTsmTargetId(t.id)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg border text-left ${tsmTargetId === t.id ? 'border-secondary bg-secondary/10' : 'border-border hover:bg-muted'}`}
                          data-testid={`tsm-table-${t.id}`}>
                    <span className="font-medium text-foreground">{t.name}</span>
                    <span className="text-xs text-muted-foreground">{t.section} · {t.capacity} seats</span>
                  </button>
                ));
              })()}

              {/* SPLIT: item checkboxes + optional target table */}
              {tsmMode === 'split' && (
                <>
                  {ticketItems.map(i => (
                    <label key={i.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer ${selectedItemIds.includes(i.id) ? 'border-secondary bg-secondary/10' : 'border-border'}`}>
                      <input type="checkbox" checked={selectedItemIds.includes(i.id)} onChange={() => toggleItem(i.id)} data-testid={`tsm-item-${i.id}`} />
                      <span className="flex-1 text-sm text-foreground">{i.quantity}× {i.name}</span>
                      <span className="text-sm text-secondary">₱{((i.line_total || 0) - (i.discount_amount || 0)).toFixed(2)}</span>
                    </label>
                  ))}
                  <div className="pt-2">
                    <p className="text-xs text-muted-foreground mb-1">Move split items to (optional):</p>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => setTsmTargetId(null)}
                              className={`text-xs px-2 py-1 rounded border ${!tsmTargetId ? 'border-secondary bg-secondary/10 text-secondary' : 'border-border text-muted-foreground'}`}>
                        New bill (no table)
                      </button>
                      {otherTables.filter(t => (t.display_status || t.status) === 'available').map(t => (
                        <button key={t.id} onClick={() => setTsmTargetId(t.id)}
                                className={`text-xs px-2 py-1 rounded border ${tsmTargetId === t.id ? 'border-secondary bg-secondary/10 text-secondary' : 'border-border text-muted-foreground'}`}>
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* MERGE: other tables with an open order */}
              {tsmMode === 'merge' && (() => {
                const occupied = otherTables.filter(t => t.current_ticket_id);
                if (!occupied.length) return <p className="text-sm text-muted-foreground text-center py-4">No other tables have an open order.</p>;
                return occupied.map(t => (
                  <button key={t.id} onClick={() => setTsmTargetId(t.id)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg border text-left ${tsmTargetId === t.id ? 'border-secondary bg-secondary/10' : 'border-border hover:bg-muted'}`}
                          data-testid={`tsm-merge-${t.id}`}>
                    <span className="font-medium text-foreground">{t.name}</span>
                    <span className="text-xs text-muted-foreground">{t.ticket_summary?.item_count ?? 0} items · ₱{(t.ticket_summary?.total ?? 0).toFixed(2)}</span>
                  </button>
                ));
              })()}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeTsm} className="border-border">Cancel</Button>
              <Button
                onClick={tsmMode === 'transfer' ? doTransfer : tsmMode === 'split' ? doSplit : doMerge}
                disabled={tsmBusy}
                className="bg-secondary hover:bg-secondary/90"
                data-testid="tsm-confirm-btn"
              >
                {tsmBusy ? 'Working…' : tsmMode === 'transfer' ? 'Transfer' : tsmMode === 'split' ? 'Split Bill' : 'Merge'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
